/**
 * Companion chest-mic — the panel Listen/mic control (round 6).
 *
 * Shared by CompanionWidget and the port-3000 presentation page so the
 * demo does not invent a third mic button. Same glyph, phase colours,
 * `data-camlin-companion-panel="chest-mic"` hook, and startVoiceTurn path.
 */

import React from 'react';

import { CAMLIN_COMPANION_ATTR } from './attributes';

export type CompanionVoicePhase = 'idle' | 'starting' | 'listening' | 'processing' | 'done';

export const COMPANION_CHEST_MIC_SIZE_PX = 56;

export function MicIcon({ size = 16 }: { size?: number }): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

export function buildChestMicStyle(phase: CompanionVoicePhase, enabled: boolean): React.CSSProperties {
  const background = phase === 'listening'
    ? '#dc2626'
    : phase === 'starting' || phase === 'processing'
      ? '#b45309'
      : phase === 'done'
        ? '#15803d'
        : 'rgba(15,23,42,0.62)';
  return {
    position: 'absolute',
    left: '50%',
    bottom: '12px',
    transform: 'translateX(-50%)',
    width: `${COMPANION_CHEST_MIC_SIZE_PX}px`,
    height: `${COMPANION_CHEST_MIC_SIZE_PX}px`,
    borderRadius: '999px',
    border: phase === 'idle'
      ? `1.5px solid ${enabled ? 'rgba(165,180,252,0.85)' : 'rgba(226,232,240,0.5)'}`
      : 'none',
    background,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: enabled ? 'pointer' : 'default',
    opacity: enabled || phase !== 'idle' ? 1 : 0.55,
    boxShadow: '0 6px 20px rgba(2,6,23,0.45)',
    zIndex: 25,
    padding: 0,
    pointerEvents: 'auto'
  };
}

export interface CompanionChestMicProps {
  voicePhase: CompanionVoicePhase;
  enabled: boolean;
  listening: boolean;
  disabled: boolean;
  onClick: () => void;
  ariaLabel: string;
  title: string;
}

export function CompanionChestMic({
  voicePhase,
  enabled,
  listening,
  disabled,
  onClick,
  ariaLabel,
  title
}: CompanionChestMicProps): React.ReactElement {
  return (
    <button
      type="button"
      {...{ [CAMLIN_COMPANION_ATTR.panel]: 'chest-mic' }}
      className={
        listening
          ? 'camlin-companion-mic-live'
          : voicePhase === 'idle' && enabled
            ? 'camlin-companion-mic-ready'
            : undefined
      }
      style={buildChestMicStyle(voicePhase, enabled)}
      aria-label={ariaLabel}
      title={title}
      disabled={disabled}
      onClick={onClick}
    >
      <MicIcon size={24} />
    </button>
  );
}

export default CompanionChestMic;
